import AOS from 'aos';
import 'aos/dist/aos.css';
import CategoryCard from "../components/CategoryCard";
import Footer from "../components/Footer";
import { useEffect, useState } from "react";
import axios from "axios";
import ProductModal from "../components/ProductModal";
import FeaturedCarousel from "../components/FeaturedCarousel";
import BlogCard from "../components/BlogCard";
import { Gift, Settings, Truck, Clock, Puzzle, ShieldCheck } from "lucide-react";
import { NavLink} from "react-router-dom";
import { useCart } from "../context/CartContext";
import AlertModal from "../components/AlertModal";

const images = [
  "/images/hero/hero1.jpg",
  "/images/hero/hero2.jpg",
  "/images/hero/hero3.jpg",
  "/images/hero/hero4.jpg",
];

function Home() {
  const [index, setIndex] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage] = useState("");
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  
  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const {
    selectedAccessories,
    setSelectedAccessories,
    selectedVips,
    setSelectedVips,
    selectedFlowers,
    setSelectedFlowers,
    removeAccessory,
  } = useCart();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/products`); // Adjust path if needed
        const all = res.data;
        const featured = all.filter((p) => p.featured);

        // Shuffle
        for (let i = featured.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [featured[i], featured[j]] = [featured[j], featured[i]];
        }

        setFeaturedProducts(featured);
      } catch (err) {
        console.error("Failed to fetch featured products:", err);
      }
    };

    fetchFeatured();
  }, [BASE_URL]);

  // Fetch blogs from backend
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/blogs`); // public blogs endpoint
        if (!res.ok) throw new Error("Failed to fetch blogs");
        const data = await res.json();
        setPosts(data);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      }
    };

    fetchBlogs();
  }, [BASE_URL]);


  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const handleAddClick = (product) => {
    if (product.category === "flower") {
      // Toggle flower: allow multiple selections like accessories & VIPs
      const alreadySelected = selectedFlowers.some(
        (item) => item._id === product._id
      );

      if (alreadySelected) {
        setSelectedFlowers((prev) =>
          prev.filter((item) => item._id !== product._id)
        );
      } else {
        setSelectedFlowers((prev) => [...prev, product]);
      }
    }

    if (product.category === "accessory") {
      // Toggle accessory
      const alreadySelected = selectedAccessories.some(
        (item) => item._id === product._id
      );

      if (alreadySelected) {
        setSelectedAccessories((prev) =>
          prev.filter((item) => item._id !== product._id)
        );
      } else {
        setSelectedAccessories((prev) => [...prev, product]);
      }
    }

    if (product.category === "vip") {
      // Toggle VIP accessory
      const alreadySelected = selectedVips.some(
        (item) => item._id === product._id
      );

      if (alreadySelected) {
        setSelectedVips((prev) =>
          prev.filter((item) => item._id !== product._id)
        );
      } else {
        setSelectedVips((prev) => [...prev, product]);
      }
    }
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="space-y-24">
        {/* Hero Section */}
        <section className="relative h-[600px] w-full overflow-hidden" data-aos="fade-down">
          {images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Hero Background ${i + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                i === index ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/40 text-white text-center px-4">
            <h1 className="text-5xl font-bold max-w-3xl mx-auto leading-tight" data-aos="fade-up">
              Deliver Joy with Every Hamper
            </h1>
            <p className="mt-4 text-xl" data-aos="fade-up">
              Free same-day flower hamper delivery, elegantly paired with premium accessories.
            </p>
            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <a href="/shop" className="w-full sm:w-[240px]">
                <button className="w-full bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition">
                  Start Shopping Now
                </button>
              </a>
              <a href="/build" className="w-full sm:w-[240px]">
                <button className="w-full bg-white text-blue-600 px-6 py-3 rounded font-medium hover:bg-gray-100 transition">
                  Build Your Hamper Now
                </button>
              </a>
            </div>
          </div>
        </section>

        {/* Category Cards */}
        <section className="px-6">
          <h2 className="text-3xl font-bold text-center mb-4" data-aos="fade-up">
            Explore Gift Categories
          </h2>
          <p
            className="text-center text-gray-500 max-w-2xl mx-auto mb-10"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Choose a hamper type to start building a custom gift. Pair elegant accessories with
            fresh flowers, or shop exclusive VIP items. Click "Get Started" to begin.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-aos="fade-up" data-aos-delay="100">
            <CategoryCard
              title="Blossom Delight - Flower Gift + 1 Accessory (3% OFF)"
              image="/images/category-card/accessory.jpg"
              description="Perfect for simple, stylish surprises."
              hamperType="BlossomDelight"
            />
            <CategoryCard
              title="Charming Bloom - Flower Gift + 2 Accessories (4% OFF)"
              image="/images/category-card/accessories.jpg"
              description="Two accessories, double the delight."
              hamperType="CharmingBloom"
            />
            <CategoryCard
              title="VIP Opulence - Flower Gift + VIP Collections (5% OFF)"
              image="/images/category-card/vip.jpg"
              description="Premium designer gifts for special moments."
              hamperType="VIPOpulence"
            />
          </div>
        </section>

        {/* Featured Carousel */}
        <section className="px-6 mt-24" data-aos="fade-up">
          <FeaturedCarousel
            products={featuredProducts}
            onClick={handleAddClick}
            onView={handleViewProduct}
            selectedItems={selectedAccessories}
            hasExistingAccessories={selectedAccessories.length > 0}
            removeAllAccessories={() => {
              selectedAccessories.forEach((item) => removeAccessory(item._id));
            }}
          />
        </section>

        {/* Gifting Made Simple */}
        <section className="py-16 px-6 bg-gray-100" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-center mb-10">Gifting Made Simple</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto text-center">
            <div className="p-10 bg-white rounded shadow">
              <Gift className="mx-auto text-blue-600 mb-4" size={72} />
              <h3 className="text-xl font-semibold mb-2">1. Choose a Gift</h3>
              <p className="text-gray-600">Browse our shop and pick from our elegant gift categories.</p>
            </div>
            <div className="p-10 bg-white rounded shadow">
              <Settings className="mx-auto text-blue-600 mb-4" size={72} />
              <h3 className="text-xl font-semibold mb-2">2. Customize</h3>
              <p className="text-gray-600">
                Select a flower, add an accessory/accessories or go without flowers. Personalize it your way.
              </p>
            </div>
            <div className="p-10 bg-white rounded shadow">
              <Truck className="mx-auto text-blue-600 mb-4" size={72} />
              <h3 className="text-xl font-semibold mb-2">3. Same-Day Delivery</h3>
              <p className="text-gray-600">We offer free same-day deliveries (orders before 3.00 PM) – fast, fresh, and right to your door.</p>
            </div>
          </div>
          <div className="text-center mt-10">
            <a href="/shop">
              <button className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition">
                Start Shopping
              </button>
            </a>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="bg-blue-50 py-16 px-6" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-center mb-10">Why Choose Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto text-center">
            <div className="p-10 bg-white rounded shadow" data-aos="fade-up" data-aos-delay="0">
              <Clock className="mx-auto text-blue-600 mb-4" size={72} />
              <h3 className="text-xl font-semibold">Free Same-Day Delivery</h3>
              <p className="text-gray-600 mt-2">
                Free same-day delivery (orders before 3.00 PM) within 50 miles of Dallas (17200 Westgrove Dr, Addison, TX).
              </p>
            </div>
            <div className="p-10 bg-white rounded shadow" data-aos="fade-up" data-aos-delay="150">
              <Puzzle className="mx-auto text-blue-600 mb-4" size={72} />
              <h3 className="text-xl font-semibold">Customizable Gift Combos</h3>
              <p className="text-gray-600 mt-2">Select accessories to pair with any bouquet.</p>
            </div>
            <div className="p-10 bg-white rounded shadow" data-aos="fade-up" data-aos-delay="300">
              <ShieldCheck className="mx-auto text-blue-600 mb-4" size={72} />
              <h3 className="text-xl font-semibold">Top-Quality Guaranteed</h3>
              <p className="text-gray-600 mt-2">We partner with trusted premium brands.</p>
            </div>
          </div>
        </section>

        {/* Blog Highlights */}
        <section className="px-6 max-w-6xl mx-auto mt-20">
          <h2 className="text-3xl font-bold mb-10 text-center" data-aos="fade-up">
            From Our Blog
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.slice(0, 3).map((post, i) => (
              <BlogCard
                key={post._id}
                image={post.image}
                title={post.title}
                excerpt={post.excerpt}
                category={post.category}
                to={`/blog/${post.slug}`} // slug for detail page
                data-aos="fade-up"
                data-aos-delay={i * 150}
              />
            ))}
          </div>
          <div className="text-center mt-10" data-aos="fade-up" data-aos-delay="500">
            <NavLink to="/blog">
              <button className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition font-medium">
                See All Blog Posts
              </button>
            </NavLink>
          </div>
        </section>

        {/* Floating CTA */}
        <section className="bg-blue-600 text-white py-12 text-center relative overflow-hidden" data-aos="fade-up">
          <div className="floating-shapes">
            {Array.from({ length: 18 }).map((_, i) => {
              const size = Math.floor(Math.random() * 60) + 30;
              const left = Math.random() * 100;
              const delay = Math.random() * 5;
              const duration = Math.random() * 2 + 3;
              return (
                <div
                  key={i}
                  className="floating-circle"
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    left: `${left}%`,
                    animationDelay: `${delay}s`,
                    animationDuration: `${duration}s`,
                  }}
                ></div>
              );
            })}
          </div>
          <h2 className="text-3xl font-bold mb-2">Every Gift Tells a Story</h2>
          <p className="text-lg mb-6">Celebrate birthdays, anniversaries, or just make someone smile today.</p>
          <a href="/build">
            <button className="bg-white text-blue-600 px-6 py-3 rounded font-medium hover:bg-gray-100 transition">
              Build Your Hamper Now
            </button>
          </a>
        </section>

        {/* Testimonials */}
        <section className="py-16 px-6" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-center mb-10">What Our Customers Are Saying</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                name: "Amina J.",
                review: "Beautiful presentation and right on time. My mum cried tears of joy. Will order again!",
                image: "/images/testimonials/amina.jpg",
              },
              {
                name: "Chris O.",
                review: "The accessories were premium quality and the flowers were fresh. Very impressed.",
                image: "/images/testimonials/chris.jpg",
              },
              {
                name: "Tolu M.",
                review: "Fast delivery, great customer service, and perfect packaging. Highly recommended!",
                image: "/images/testimonials/tolu.jpg",
              },
            ].map((testimonial, index) => (
              <div key={index} className="bg-white shadow rounded p-6 text-center">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-2 border-blue-500"
                />
                <p className="text-gray-600 italic mb-4">"{testimonial.review}"</p>
                <h4 className="font-semibold text-lg">{testimonial.name}</h4>
              </div>
            ))}
          </div>
        </section>

        <Footer />
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlert}
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddClick}
      />
    </>
  );
}

export default Home;
